<template>
  <ConfirmModal v-model="showConfirmationModal" message="Are you sure you want to submit this invoice?"
    @confirm="submitFormData" />
  <viewServiceInvoice />
  <main v-if="localShow" class="fixed inset-0 z-50 flex items-center justify-center font-syne px-4 sm:px-6"
    :style="{ fontFamily: `'Syne', sans-serif` }">
    <!-- Overlay -->
    <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

    <!-- Modal Content -->
    <div
      class="relative bg-white dark:bg-gray-900/90 backdrop-blur-lg rounded-2xl shadow-xl w-full max-w-3xl sm:w-[693px] p-6 sm:p-8 flex flex-col max-h-[90vh] text-gray-900 dark:text-gray-100 transition-colors duration-300">
      <!-- Header with X button -->
      <div
        class="flex justify-between items-center border-b-2 pb-2 mb-7 border-[#e87055] dark:border-[#f97316] transition-colors duration-300">
        <h3 class="text-xl font-semibold">Create Service Invoice</h3>
        <button
          class="transition bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white font-bold px-3 py-1 rounded-full shadow-lg focus:outline-none"
          @click="close">
          ✕
        </button>
      </div>

      <!-- Inline Loading Overlay -->
      <div v-if="isLoading"
        class="absolute rounded-lg inset-0 bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm z-50 flex flex-col justify-center items-center gap-4">
        <!-- Spinner -->
        <div class="w-12 h-12 border-4 border-brand_orange2 border-t-transparent rounded-full animate-spin"></div>
        <!-- Loading Text -->
        <p class="text-gray-700 dark:text-gray-200 font-medium text-lg">
          Processing...
        </p>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto">
        <form class="space-y-6 mb-6">
          <div v-for="(section, sIndex) in formSections" :key="sIndex" class="mb-6">
            <!-- Section Title -->
            <div class="flex items-baseline mb-4" v-if="section.title">
              <h4 class="text-lg font-semibold mr-3 dark:text-gray-200">
                {{ section.title }}
              </h4>
              <div class="flex-1 h-0.5 bg-[#e87055] dark:bg-[#f97316]"></div>
            </div>

            <!-- Fields -->
            <div class="space-y-4">
              <div v-for="(row, rIndex) in section.fields" :key="rIndex" class="flex flex-col sm:flex-row sm:space-x-4">
                <div v-for="(field, fIndex) in row" :key="fIndex" class="flex-1 relative">
                  <invoiceFormFields :field="field" :form-data="formData" :errors="errors"
                    :new-description="newDescription" @blur-description="autoAddItemDescription" @update:newDescription="
                      (newVal) => (newDescription = newVal)
                    " @update:soldToData="
                      (data) => {
                        formData.soldTo = data.soldTo || formData.soldTo;
                        if (data.tin !== undefined) formData.tin = data.tin;
                        if (data.address !== undefined)
                          formData.address = data.address;
                      }
                    " />
                </div>
              </div>
            </div>
          </div>
        </form>

        <!-- Auto Compute Section -->
        <div class="flex items-baseline mb-4">
          <h4 class="text-lg font-semibold mr-3 dark:text-gray-200">
            Invoice Totals
          </h4>
          <div class="flex-1 h-0.5 bg-[#e87055] dark:bg-[#f97316]"></div>
        </div>

        <!-- Auto Compute Fields -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <div class="flex flex-col">
            <span>Amount :</span>
            <span :class="computedResult.amount && computedResult.amount !== 0
              ? 'text-[#28a745] dark:text-green-400'
              : 'dark:text-gray-300'
              ">
              {{ formatPeso(computedResult.amount) }}
            </span>
          </div>

          <div class="flex flex-col">
            <span>VAT Amount :</span>
            <span :class="computedResult.vatAmount && computedResult.vatAmount !== 0
              ? 'text-[#28a745] dark:text-green-400'
              : 'dark:text-gray-300'
              ">
              {{ formatPeso(computedResult.vatAmount) }}
            </span>
          </div>

          <div class="flex flex-col">
            <span>Total Sales (VAT Inclusive) :</span>
            <span :class="computedResult.totalSales && computedResult.totalSales !== 0
              ? 'text-[#28a745] dark:text-green-400'
              : 'dark:text-gray-300'
              ">
              {{ formatPeso(computedResult.totalSales) }}
            </span>
          </div>

          <div class="flex flex-col">
            <span>Less : SC/PWD Discount :</span>
            <span :class="computedResult.scPwdDiscount &&
              computedResult.scPwdDiscount !== 0
              ? 'text-[#28a745] dark:text-green-400'
              : 'dark:text-gray-300'
              ">
              {{ formatPeso(computedResult.scPwdDiscount) }}
            </span>
          </div>

          <div class="flex flex-col">
            <span>Less : VAT :</span>
            <span :class="computedResult.vat && computedResult.vat !== 0
              ? 'text-[#28a745] dark:text-green-400'
              : 'dark:text-gray-300'
              ">
              {{ formatPeso(computedResult.vat) }}
            </span>
          </div>

          <div class="flex flex-col">
            <span>Less : Withholding Tax :</span>
            <span :class="computedResult.lessWithholding &&
              computedResult.lessWithholding !== 0
              ? 'text-[#28a745] dark:text-green-400'
              : 'dark:text-gray-300'
              ">
              {{ formatPeso(computedResult.lessWithholding) }}
            </span>
          </div>

          <div class="flex flex-col">
            <span>Net Of Vat :</span>
            <span :class="computedResult.netOfVat && computedResult.netOfVat !== 0
              ? 'text-[#28a745] dark:text-green-400'
              : 'dark:text-gray-300'
              ">
              {{ formatPeso(computedResult.netOfVat) }}
            </span>
          </div>

          <div class="flex flex-col font-bold">
            <span>Total Amount Due :</span>
            <span :class="computedResult.totalAmountDue &&
              computedResult.totalAmountDue !== 0
              ? 'text-[#28a745] dark:text-green-400'
              : 'dark:text-gray-200'
              ">
              {{ formatPeso(computedResult.totalAmountDue) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="mt-4 border-t pt-4 flex flex-col sm:flex-row justify-between items-stretch sm:items-center border-gray-300 dark:border-gray-600 transition-colors duration-300 gap-3 sm:gap-0">
        <!-- Left side: Pre-Fill Toggle Button -->
        <button @click="togglePrefill" :class="[
          'px-4 py-2 rounded-md text-white w-full sm:w-32 hover:brightness-90 transition-colors duration-300',
          hasPrefill
            ? 'bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700'
            : 'bg-[#3b82f6] hover:bg-[#2563eb] dark:bg-blue-600 dark:hover:bg-blue-500',
        ]">
          {{ hasPrefill ? "Clear Preset" : "Save Preset" }}
        </button>

        <!-- Right side: Save Buttons -->
        <div class="flex flex-col sm:flex-row sm:space-x-3 space-y-3 sm:space-y-0 w-full sm:w-auto">
          <button v-if="!prefillMode" @click="validateThenConfirm(true)"
            class="px-4 py-2 rounded-md w-full sm:w-auto bg-[#f59e0b] text-white hover:bg-[#d97706] dark:bg-yellow-600 dark:hover:bg-yellow-500 transition-colors duration-300">
            Save & Create New
          </button>
          <button v-if="!prefillMode" @click="validateThenConfirm(false)"
            class="px-4 py-2 rounded-md w-full sm:w-auto bg-[#e87055] text-white hover:bg-[#d75b40] dark:bg-orange-600 dark:hover:bg-orange-500 transition-colors duration-300">
            Save & Close
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { computed, reactive, ref, inject, watch, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import { invoiceSchema } from "../../utils/formValidators";
import ConfirmModal from "@/components/modals/confirmation.vue";
import loading from "./loading.vue";
import invoiceFormFields from "../partial/ServiceInvoice/invoiceFormFields.vue";
import viewServiceInvoice from "@/components/modals/viewServiceInvoice.vue";

export default {
  name: "CreateServiceInvoiceForm",
  components: { ConfirmModal, loading, invoiceFormFields, viewServiceInvoice },
  props: {
    showCreateInvoice: {
      type: Boolean,
      required: true,
    },
  },

  emits: ["update:showCreateInvoice"],

  setup(props, { emit }) {
    const axios = inject("$axios");
    const toast = useToast();

    const formData = reactive({
      invoice_number: "",
      soldTo: "",
      tin: "",
      address: "",
      terms: "",
      osca_pwd_id_no: "",
      sc_pwd_signature: "",
      invoice_date: "",
      modeOfPayment: "Cash",
      withWithholding: false,
      vatableSalesRaw: 0,
      zeroRatedSales: 0,
      vatExemptSales: 0,
      invoice_items: [],
    });

    const errors = ref({});
    const newDescription = ref("");
    const descriptionItemIndex = ref(null); // Track index of the added description in invoice_items
    const isLoading = ref(false);
    const showConfirmationModal = ref(false);
    const confirmationCreateNew = ref(false);

    const computedResult = ref({
      amount: 0,
      vatAmount: 0,
      totalSales: 0,
      scPwdDiscount: 0,
      vat: 0,
      withholdingTax: 0,
      netOfVat: 0,
      totalAmountDue: 0,
    });

    const formSections = ref([
      {
        title: "General Information",
        fields: [
          [
            {
              label: "Invoice Number",
              type: "text",
              model: "invoice_number",
              placeholder: "Enter invoice number",
            },
            {
              label: "Date",
              type: "date",
              model: "invoice_date",
              placeholder: "Select date",
            },
          ],
          [
            {
              label: "Sold To",
              type: "text",
              model: "soldTo",
              placeholder: "Enter payee name",
            },
            {
              label: "TIN",
              type: "text",
              model: "tin",
              placeholder: "Enter TIN",
            },
          ],
          [
            {
              label: "Address",
              type: "textarea",
              rows: 2,
              model: "address",
              placeholder: "Enter address",
            },
            {
              label: "Description",
              type: "textarea",
              rows: 2,
              model: null,
              placeholder: "Enter description",
            },
          ],
        ],
      },
      {
        title: "Payment Information",
        fields: [
          [
            {
              label: "Mode Of Payment",
              type: "select",
              model: "modeOfPayment",
              options: [
                { label: "Cash", value: "Cash" },
                { label: "Check", value: "Check" },
              ],
              placeholder: "Select mode of payment",
            },
            {
              label: "Withholding Tax",
              type: "select",
              model: "withWithholding",
              options: [
                { label: "Yes", value: true },
                { label: "No", value: false },
              ],
              placeholder: "Select withholding tax",
            },
          ],
          [
            {
              label: "VATable Sales",
              type: "number",
              model: "vatableSalesRaw",
              placeholder: "Enter VATable sales",
            },
            {
              label: "Zero Rated Sales",
              type: "number",
              model: "zeroRatedSales",
              placeholder: "Enter zero-rated sales",
            },
          ],
          [
            {
              label: "VAT-Exempt Sales",
              type: "number",
              model: "vatExemptSales",
              placeholder: "Enter VAT-exempt sales",
            },
            {
              label: "",
              type: "empty",
            },
          ],
        ],
      },
    ]);

    const localShow = computed({
      get() {
        return props.showCreateInvoice;
      },
      set(value) {
        emit("update:showCreateInvoice", value);
      },
    });

    const resetForm = () => {
      // Reset all fields to default
      Object.assign(formData, {
        invoice_number: "",
        soldTo: "",
        tin: "",
        address: "",
        terms: "",
        osca_pwd_id_no: "",
        sc_pwd_signature: "",
        invoice_date: "",
        modeOfPayment: "Cash",
        withWithholding: false,
        vatableSalesRaw: 0,
        zeroRatedSales: 0,
        vatExemptSales: 0,
        invoice_items: [],
      });

      newDescription.value = "";
      descriptionItemIndex.value = null;

      // Restore prefill from localStorage if available
      const savedPrefill = localStorage.getItem("prefillInvoice");
      if (savedPrefill) {
        const parsed = JSON.parse(savedPrefill);
        Object.keys(formData).forEach((key) => {
          if (parsed[key] !== undefined) {
            formData[key] = Array.isArray(parsed[key])
              ? [...parsed[key]]
              : parsed[key];
          }
        });
      }
    };

    const close = () => {
      localShow.value = false;

      const savedPrefill = localStorage.getItem("prefillInvoice");
      if (savedPrefill) {
        // Load the prefill instead of resetting to empty
        const parsed = JSON.parse(savedPrefill);

        Object.keys(formData).forEach((key) => {
          if (parsed[key] !== undefined) {
            formData[key] = Array.isArray(parsed[key])
              ? [...parsed[key]]
              : parsed[key];
          } else {
            // fallback to empty/default value
            if (Array.isArray(formData[key])) formData[key] = [];
            else if (typeof formData[key] === "number") formData[key] = 0;
            else if (typeof formData[key] === "boolean") formData[key] = false;
            else formData[key] = "";
          }
        });
      } else {
        // No prefill, reset to default empty values
        Object.assign(formData, {
          invoice_number: "",
          soldTo: "",
          tin: "",
          address: "",
          terms: "",
          osca_pwd_id_no: "",
          sc_pwd_signature: "",
          invoice_date: "",
          modeOfPayment: "Cash",
          withWithholding: false,
          vatableSalesRaw: 0,
          zeroRatedSales: 0,
          vatExemptSales: 0,
          invoice_items: [],
        });
      }

      // reset helper refs
      newDescription.value = "";
      descriptionItemIndex.value = null;
    };

    const autoAddItemDescription = () => {
      const description = newDescription.value.trim();

      // If empty and we previously added an item, remove it
      if (!description && descriptionItemIndex.value !== null) {
        formData.invoice_items.splice(descriptionItemIndex.value, 1);
        descriptionItemIndex.value = null;
        console.log("Description removed");
        return;
      }

      // If description is not empty
      if (description) {
        const newItem = {
          description,
          quantity: 0,
          cost: 0,
          items_total: 0,
        };

        if (descriptionItemIndex.value !== null) {
          // Update existing description object
          formData.invoice_items[descriptionItemIndex.value] = newItem;
          console.log("Description updated:", newItem);
        } else {
          // Add new description object
          formData.invoice_items.push(newItem);
          descriptionItemIndex.value = formData.invoice_items.length - 1;
          console.log("Description added:", newItem);
        }
      }
    };

    const validateThenConfirm = async (createNew = false) => {
      console.log("Clicked");
      errors.value = {}; // reset errors
      confirmationCreateNew.value = createNew; // track button clicked

      try {
        // Validate the reactive payload
        await invoiceSchema.validate(formData, { abortEarly: false });

        // If valid → show modal
        showConfirmationModal.value = true;
      } catch (err) {
        if (err.name === "ValidationError") {
          err.inner.forEach((e) => {
            errors.value[e.path] = e.message; // store errors
          });

          toast.add({
            severity: "warn",
            summary: "Validation Error",
            detail: "Please fill in the required fields.",
            life: 3000,
          });
        }
      }
    };

    const submitFormData = async () => {
      try {
        console.log("Final Payload : ", formData);

        isLoading.value = true;

        const res = await axios.post("/api/invoice/create", formData);
        const newInvoice = res.data.data;

        console.log("Invoice Id : ");
        console.log(newInvoice.transaction.id);

        toast.add({
          severity: "success",
          summary: "Success",
          detail: "Successfully Created Service Invoice",
          life: 3000,
        });

        if (confirmationCreateNew.value) {
          // Reset form for new invoice without closing modal
          resetForm();
          showConfirmationModal.value = false;
        } else {
          // Close modal and reset fields
          localShow.value = false;
          resetForm();

          emit("created", newInvoice.transaction.id);
        }
      } catch (err) {
        console.error("API Error:", err.response?.data || err.message);

        toast.add({
          severity: "error",
          summary: "Error",
          detail: err.response?.data?.message || err.message,
          life: 3000,
        });
      } finally {
        isLoading.value = false;
      }
    };

    const computeTransaction = async () => {
      if (
        !formData.vatableSalesRaw &&
        !formData.zeroRatedSales &&
        !formData.vatExemptSales
      ) {
        computedResult.value = {};
        return;
      }

      try {
        const res = await axios.get(
          "/api/invoice/get/transaction/computation",
          {
            params: {
              withWithholding: formData.withWithholding,
              vatableSalesRaw: formData.vatableSalesRaw,
              zeroRatedSales: formData.zeroRatedSales,
              vatExemptSales: formData.vatExemptSales,
            },
          }
        );
        computedResult.value = res.data.data || {};
        console.log("Computed Result:", computedResult.value);
      } catch (error) {
        console.error("Error computing transaction:", error);
      }
    };

    watch(
      () => [
        formData.vatableSalesRaw,
        formData.zeroRatedSales,
        formData.vatExemptSales,
        formData.withWithholding,
      ],
      computeTransaction,
      { immediate: true }
    );

    const formatNumber = (value) => {
      if (value === null || value === undefined) return "0.00";
      return Number(value)
        .toFixed(2) // ensures 2 decimals
        .replace(/\B(?=(\d{3})+(?!\d))/g, ","); // adds commas
    };

    const formatPeso = (value) => {
      return `₱ ${formatNumber(value)}`;
    };

    // ---------  PREFIL FORM PRESET FEATURE ------------

    const savePrefill = () => {
      // Example: store current formData in localStorage
      localStorage.setItem("prefillInvoice", JSON.stringify(formData));

      toast.add({
        severity: "success",
        summary: "Saved",
        detail: "Prefill form presets successfully saved.",
        life: 3000,
      });
    };

    const loadFormPreset = () => {
      const savedPrefill = localStorage.getItem("prefillInvoice");
      if (savedPrefill) {
        const parsed = JSON.parse(savedPrefill);

        Object.keys(formData).forEach((key) => {
          if (parsed[key] !== undefined) {
            // For arrays, replace the array entirely to maintain reactivity
            if (Array.isArray(parsed[key])) {
              formData[key] = [...parsed[key]];
            } else {
              formData[key] = parsed[key];
            }
          }
        });
      }
    };

    const hasPrefill = ref(false);

    const togglePrefill = () => {
      if (hasPrefill.value) {
        // Clear the preset
        localStorage.removeItem("prefillInvoice");
        hasPrefill.value = false;

        // Clear the form data immediately
        Object.assign(formData, {
          invoice_number: "",
          soldTo: "",
          tin: "",
          address: "",
          terms: "",
          osca_pwd_id_no: "",
          sc_pwd_signature: "",
          invoice_date: "",
          modeOfPayment: "Cash",
          withWithholding: false,
          vatableSalesRaw: 0,
          zeroRatedSales: 0,
          vatExemptSales: 0,
          invoice_items: [],
        });

        newDescription.value = "";
        descriptionItemIndex.value = null;

        toast.add({
          severity: "info",
          summary: "Cleared",
          detail: "Prefill preset has been cleared.",
          life: 3000,
        });
      } else {
        // Save current formData as preset
        localStorage.setItem("prefillInvoice", JSON.stringify(formData));
        hasPrefill.value = true;
        toast.add({
          severity: "success",
          summary: "Saved",
          detail: "Prefill form presets successfully saved.",
          life: 3000,
        });
      }
    };

    onMounted(() => {
      hasPrefill.value = !!localStorage.getItem("prefillInvoice");
      loadFormPreset();
    });

    return {
      // ---------  PREFIL FORM PRESET FEATURE ------------
      hasPrefill,
      savePrefill,
      togglePrefill,
      // --------------------------------------------------
      formData,
      formSections,
      localShow,
      isLoading,
      errors,
      showConfirmationModal,
      newDescription,
      computedResult,
      close,
      submitFormData,
      validateThenConfirm,
      autoAddItemDescription,
      computeTransaction,
      formatNumber,
      formatPeso,
    };
  },
};
</script>
